import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import EmblaCarousel from "@tFissco/Components/EmblaCarousel";
import Circle from "@tFissco/Icons/Circle";
import Line from "@tFissco/Icons/Line";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    links = "links",
    autoplay = "autoplay",
    maximumNumberOfSuppliersPerRow = "maximumNumberOfSuppliersPerRow",
    maximumSupplierImageHeight = "maximumSupplierImageHeight",
    buttonLabel = "buttonLabel",
    buttonVariant = "variant",
}

type Props = OwnProps;
interface LinkModel {
    fields: {
        title: string;
        logoImage: string;
        children?: LinkModel[];
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.links]: LinkModel[];
        [fields.maximumNumberOfSuppliersPerRow]: string;
        [fields.maximumSupplierImageHeight]: number;
        [fields.autoplay]: number;
        [fields.buttonLabel]: string;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
    };
}

export interface SupplierStyles {
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    image?: LazyImageProps;
    supplierListButton?: InjectableCss;
    titleText?: TypographyPresentationProps;
}

export const supplierStyles: SupplierStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            border: 0.5px solid ${getColor("common.border")};
            border-radius: 10px;
            box-shadow: 0px 6px 6px rgba(0, 0, 0, 0.25);
            min-width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0;
                    `,
                    css`
                        padding: 0;
                    `,
                    css`
                        padding: 1rem;
                    `,
                    css`
                        padding: 1rem;
                    `,
                    css`
                        padding: 1.5rem 1.3rem;
                    `,
                ])}
        `,
    },
    titleText: {
        variant: "h2",
        color: "primary",
        css: css`
            text-align: center;
            margin: 1rem 0;
            font-weight: bold;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    imageContainer: {
        css: css`
            > ${GridWrapper} {
                > ${GridOffset} {
                    max-width: 100%;
                }
            }
        `,
    },
    imageItem: {
        css: css`
            padding: 5px;
            justify-content: center;
            flex: 0 0 auto;
            padding: 0;
            position: relative;
            flex-basis: 20%;
            max-width: 20%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 50%;
                    `,
                    css`
                        width: 20%;
                    `,
                    css`
                        width: 20%;
                    `,
                    css`
                        width: 20%;
                    `,
                    css`
                        width: 20%;
                    `,
                ])}
        `,
    },
    supplierListButton: {
        css: css`
            /* border-radius: 10px; */
            width: fit-content;
            margin: 1rem auto;
        `,
    },
};

const styles = supplierStyles;
export const FisscoSuppliers = ({ fields }: Props) => {
    const desktop = 5;
    const tablet = Math.ceil(desktop / 2);
    const mobile = 1;
    const imgProps = {
        style: {
            width: "auto",
            maxWidth: "100%",
            maxHeight: `${fields.maximumSupplierImageHeight}px`,
        },
    };
    return (
        <>
            <StyledWrapper {...styles.container} data-test-selector="suppliers">
                <Typography {...styles.titleText}>
                    <Line />
                    <Circle /> &nbsp;&nbsp;<Typography>{fields.title}</Typography>&nbsp;&nbsp;
                    <Circle />
                    <Line />
                </Typography>
                <FlexWrapContainer {...styles.imageContainer}>
                    <EmblaCarousel valuePass={fields.autoplay}>
                        {fields.links.map((imageLink, index) => (
                            <FlexItem
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                flexColumns={[mobile, tablet, tablet, desktop, desktop]}
                                {...styles.imageItem}
                            >
                                <LazyImage
                                    imgProps={imgProps}
                                    {...styles.image}
                                    src={imageLink.fields.logoImage}
                                    altText="Icon"
                                />
                            </FlexItem>
                        ))}
                    </EmblaCarousel>
                </FlexWrapContainer>
                <Button {...styles.supplierListButton} variant={fields.variant}>
                    {fields.buttonLabel}
                </Button>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: FisscoSuppliers,
    definition: {
        group: "Basic",
        displayName: "Fissco Suppliers",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "logoImage",
                        displayName: "Logo Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                ],
            },
            // {
            //     name: fields.maximumNumberOfSuppliersPerRow,
            //     displayName: "Maximum suppliers per row",
            //     editorTemplate: "DropDownField",
            //     defaultValue: "6",
            //     options: [
            //         { displayName: "1", value: "1" },
            //         { displayName: "2", value: "2" },
            //         { displayName: "3", value: "3" },
            //         { displayName: "4", value: "4" },
            //         { displayName: "5", value: "5" },
            //         { displayName: "6", value: "6" },
            //     ],
            //     fieldType: "General",
            //     tooltip:
            //         "Up to 6 suppliers can display per row. This number should be adjusted based on the number of suppliers you choose to display. For example, if you display 10 suppliers you likely want 5 to display per row. Logos will also stack responsively for smaller screen sizes.",
            // },
            // {
            //     name: fields.maximumSupplierImageHeight,
            //     displayName: "Maximum suppliers image height",
            //     editorTemplate: "IntegerField",
            //     defaultValue: 100,
            //     fieldType: "General",
            //     tooltip:
            //         "By default, images will scale to meet the width of the screen for the number of suppliers to display per row. The maximum height value can be used to scale suppliers logos to be smaller. Default value is 100 pixels.",
            // },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                min: 1,
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
